import * as THREE from 'three';
import { CubeFace } from '../Types';
import { LODController, TransitionState, LODChangeEvent } from './LODController';
import { ViewportService } from './ViewportService';
import { SceneAdapter } from './SceneAdapter';
import { ReplacementPolicy } from './ReplacementPolicy';
import { SpatialIndex } from './SpatialIndex';
import { TIMING, COVERAGE } from './constants';
import { processBatches } from './utils';

/**
 * Transition completion callback
 */
export type TransitionCompleteCallback = () => void;

/**
 * Events emitted by TransitionCoordinator
 */
export interface TransitionCoordinatorEvents {
  transitionStarted: () => void;
  transitionCompleted: () => void;
  tilesMarkedForRemoval: (tileKeys: string[]) => void;
  tilesRemoved: (tileKeys: string[]) => void;
  safetyCheckFailed: (reason: string) => void;
}

/**
 * Transition coordinator statistics
 */
export interface TransitionCoordinatorStats {
  isTransitioning: boolean;
  pendingRemovals: number;
  completedTransitions: number;
  safetyCheckFailures: number;
  averageTransitionTime: number;
  lastTransitionDuration: number;
}

/**
 * Coordinates safe tile transitions to prevent black screens
 */
export class TransitionCoordinator {
  private readonly lodController: LODController;
  private readonly viewportService: ViewportService;
  private readonly sceneAdapter: SceneAdapter;
  private readonly replacementPolicy: ReplacementPolicy;
  private readonly spatialIndex: SpatialIndex;

  // Pending hides/removals and scheduled timers
  private readonly pendingRemovalTiles = new Map<string, THREE.Mesh>();
  private readonly timeoutCancelers: Array<() => void> = [];

  // Coverage tracking for the TARGET LOD (per face)
  private readonly loadedByFace = new Map<CubeFace, Set<string>>();
  private readonly loadedAllSet = new Set<string>();
  private trackedTargetLevel: number | null = null;

  // Stats
  private completedTransitions = 0;
  private safetyCheckFailures = 0;
  private transitionTimes: number[] = [];
  private lastTransitionDuration = 0;

  // Events
  private readonly eventHandlers = new Map<keyof TransitionCoordinatorEvents, Set<Function>>();

  // Lifecycle guards
  private disposed = false;

  // Bound listeners for proper unsubscription
  private readonly onTransitionStartedBound = () => this.handleTransitionStarted();
  private readonly onLodChangedBound = (event: LODChangeEvent) => this.handleLODChanged(event);

  constructor(
    lodController: LODController,
    viewportService: ViewportService,
    sceneAdapter: SceneAdapter,
    replacementPolicy: ReplacementPolicy,
    spatialIndex: SpatialIndex
  ) {
    this.lodController = lodController;
    this.viewportService = viewportService;
    this.sceneAdapter = sceneAdapter;
    this.replacementPolicy = replacementPolicy;
    this.spatialIndex = spatialIndex;

    // Listen to LOD controller events
    this.lodController.on('transitionStarted', this.onTransitionStartedBound);
    this.lodController.on('lodChanged', this.onLodChangedBound as any);
  }

  /**
   * Handle new tile loaded - potentially trigger replacement
   */
  async handleTileLoaded(
    coordinate: { x: number; y: number; face: CubeFace; level: number },
    mesh: THREE.Mesh
  ): Promise<void> {
    if (this.disposed) return;

    const tileKey = this.buildTileKey(coordinate.face, coordinate.level, coordinate.x, coordinate.y);

    // Track coverage for TARGET LOD only
    const targetLevel = this.getTargetLevelNumber();
    if (coordinate.level === targetLevel) {
      this.trackTargetCoverage(coordinate.face, tileKey, targetLevel);
    }

    // Wait until the mesh was actually rendered at least once, unless we got disposed meanwhile
    const renderPromise = this.sceneAdapter.waitForMeshRendering(mesh);
    const disposedGate = new Promise<void>((resolve) => {
      if (this.disposed) resolve();
    });

    await Promise.race([renderPromise, disposedGate]);
    if (this.disposed) return;

    // Ask policy what to replace given the new tile
    const decision = this.replacementPolicy.decideReplacement(
      this.buildSpatialKey(coordinate.face, coordinate.x, coordinate.y),
      coordinate.level,
      this.lodController.getTargetLODLevel(),
      this.lodController.isRapidTransition()
    );

    // SAFER HIDES: only hide if that face has reached sufficient target coverage
    this.hideTilesSafely(decision.tilesToHide);

    // Queue removals (filtered by safety policy)
    this.markTilesForRemoval(decision.tilesToRemove, decision.reason);

    // Optional: remove L1 tiles for that face if policy allows and we are above level 1
    if (
      coordinate.level > 1 &&
      this.replacementPolicy.shouldRemoveLevel1ForFace(
        coordinate.face,
        coordinate.level,
        this.lodController.getTargetLODLevel(),
        this.lodController.isRapidTransition()
      )
    ) {
      this.markLevel1TilesForRemoval(coordinate.face);
    }

    // Re-evaluate completion
    this.scheduleTransitionCompletion();
  }

  /**
   * Hide tiles immediately but only when the face's target coverage is "good enough".
   */
  private hideTilesSafely(tileKeys: string[]): void {
    if (tileKeys.length === 0) return;

    const targetTilesPerFace = this.getTargetTilesPerFace();
    // Expected density per face (capped to avoid being overly strict)
    const expectedPerFace = Math.min(targetTilesPerFace * targetTilesPerFace, 9);
    const perFaceHideThreshold = Math.max(1, Math.floor(expectedPerFace * 0.7)); // ~70%

    for (const tileKey of tileKeys) {
      const parsed = this.parseTileKey(tileKey);
      if (!parsed) continue;
      const { face } = parsed;

      const faceSet = this.loadedByFace.get(face);
      const hasCoverage = !!faceSet && faceSet.size >= perFaceHideThreshold;

      if (hasCoverage) {
        const mesh = this.spatialIndex.getTile(tileKey);
        if (mesh) mesh.visible = false;
      }
      // else: keep visible until we have more target coverage on that face
    }
  }

  /**
   * Mark tiles for delayed removal
   */
  private markTilesForRemoval(tileKeys: string[], _reason: string): void {
    if (tileKeys.length === 0) return;

    const coverage = this.replacementPolicy.analyzeCoverage();

    // Baseline safety: do not start any removal if total coverage is dangerously low
    if (coverage.totalTiles < COVERAGE.MIN_SAFE_TILE_COUNT) {
      this.emit('safetyCheckFailed', 'Insufficient tiles for safe removal');
      this.safetyCheckFailures++;
      return;
    }

    // Policy-level filtering
    const safeTilesToRemove = this.replacementPolicy.filterSafeRemovals(
      tileKeys,
      this.lodController.getTransitionState() === TransitionState.TRANSITIONING
    );

    for (const tileKey of safeTilesToRemove) {
      const mesh = this.spatialIndex.getTile(tileKey);
      if (mesh && !this.pendingRemovalTiles.has(tileKey)) {
        this.pendingRemovalTiles.set(tileKey, mesh);
      }
    }

    if (safeTilesToRemove.length > 0) {
      this.emit('tilesMarkedForRemoval', safeTilesToRemove);
    }
  }

  /**
   * Remove all level 1 tiles for a specific face (with safety filtering)
   */
  private markLevel1TilesForRemoval(face: CubeFace): void {
    const level1Tiles = this.spatialIndex.getTilesForFaceAndLevel(face, 1);
    const tileKeys = Array.from(level1Tiles.keys());
    this.markTilesForRemoval(tileKeys, `Level 1 removal for face ${face}`);
  }

  private handleTransitionStarted(): void {
    this.emit('transitionStarted');
    // Reset coverage tracking to the *new* target level at transition start
    this.resetCoverageTracking();
  }

  /**
   * When LOD changes (e.g., zoom-out), immediately restore a coarse fallback per spatial area
   * so we never show black gaps while new tiles are loading/cancelled.
   */
  private handleLODChanged(_event: LODChangeEvent): void {
    // Reset per-face target coverage tracking for the new target level
    this.resetCoverageTracking();

    // For each spatial area, ensure at least one level is visible.
    // Strategy: pick the highest loaded level in that area and make its tiles visible again.
    const spatialAreas = this.spatialIndex.getSpatialAreas();
    for (const [spatialKey, levelMap] of spatialAreas) {
      if (!levelMap || levelMap.size === 0) continue;

      // Find highest available level for this area
      let highestLevel = -Infinity;
      for (const lvl of levelMap.keys()) {
        if (lvl > highestLevel) highestLevel = lvl;
      }
      if (highestLevel === -Infinity) continue;

      const tiles = levelMap.get(highestLevel);
      if (!tiles || tiles.size === 0) continue;

      // Make all tiles at that level visible (fallback coverage)
      for (const [, mesh] of tiles) {
        if (mesh) mesh.visible = true;
      }
    }
  }

  private resetCoverageTracking(): void {
    this.loadedAllSet.clear();
    this.loadedByFace.clear();
    this.trackedTargetLevel = this.getTargetLevelNumber();
  }

  private trackTargetCoverage(face: CubeFace, tileKey: string, targetLevel: number): void {
    if (this.trackedTargetLevel !== targetLevel) {
      // Target changed mid-flight; reset tracking to avoid mixed-level stats
      this.resetCoverageTracking();
      this.trackedTargetLevel = targetLevel;
    }
    let set = this.loadedByFace.get(face);
    if (!set) {
      set = new Set<string>();
      this.loadedByFace.set(face, set);
    }
    if (!set.has(tileKey)) {
      set.add(tileKey);
      this.loadedAllSet.add(tileKey);
    }
  }

  /**
   * (Re)schedule smart transition completion checks
   */
  private scheduleTransitionCompletion(): void {
    if (this.disposed) return;

    // Cancel any previously scheduled checks
    this.cancelScheduledTimers();

    const runCheck = () => {
      if (this.disposed) return;
      if (this.shouldCompleteTransition()) {
        // Defer to next tick to avoid completing inside a load callback
        const id = setTimeout(() => this.completeTransition(), 0);
        this.timeoutCancelers.push(() => clearTimeout(id));
      } else if (this.lodController.getTransitionElapsedTime() < TIMING.MAX_TRANSITION_TIME) {
        const id = setTimeout(runCheck, TIMING.TRANSITION_CHECK_INTERVAL);
        this.timeoutCancelers.push(() => clearTimeout(id));
      } else {
        // Hard timeout: complete anyway (we still apply removal safety)
        const id = setTimeout(() => this.completeTransition(), 0);
        this.timeoutCancelers.push(() => clearTimeout(id));
      }
    };

    const id = setTimeout(runCheck, TIMING.TRANSITION_INITIAL_DELAY);
    this.timeoutCancelers.push(() => clearTimeout(id));
  }

  /**
   * Decide whether the transition is ready to complete
   */
  private shouldCompleteTransition(): boolean {
    if (this.lodController.getTransitionState() !== TransitionState.TRANSITIONING) {
      return false;
    }

    const elapsed = this.lodController.getTransitionElapsedTime();
    const isTargetStable = this.lodController.isLevelStable();
    const isRapid = this.lodController.isRapidTransition();

    const tilesPerFace = this.getTargetTilesPerFace();

    const expectedPerFace = Math.min(tilesPerFace * tilesPerFace, 9);
    const faceThreshold = Math.max(1, Math.floor(expectedPerFace * 0.7)); // ~70%

    // How many faces must be "ready"
    const facesNeeded = isRapid ? 2 : 1;

    // Count how many faces meet the per-face threshold
    let facesReady = 0;
    for (const [, set] of this.loadedByFace) {
      if (set.size >= faceThreshold) facesReady++;
    }

    const timeOK = elapsed >= (isRapid ? TIMING.MIN_RAPID_TRANSITION_TIME : TIMING.MIN_TRANSITION_TIME);

    return isTargetStable && timeOK && facesReady >= facesNeeded;
  }

  /**
   * Complete the transition with safe tile removal
   */
  private async completeTransition(): Promise<void> {
    if (this.disposed) return;

    // Wait for viewport stability before destructive operations
    if (!this.viewportService.isViewportStable(TIMING.REMOVAL_STABILITY_MS)) {
      const delay = TIMING.REMOVAL_STABILITY_MS - this.viewportService.getTimeSinceLastUpdate();
      const id = setTimeout(() => this.completeTransition(), Math.max(0, delay));
      this.timeoutCancelers.push(() => clearTimeout(id));
      return;
    }

    // Remove pending tiles safely (batched)
    await this.safelyRemovePendingTiles();
    if (this.disposed) return;

    // Capture duration BEFORE telling LOD to complete (in case it resets timers)
    const duration = this.lodController.getTransitionElapsedTime();

    // Finalize the LOD
    this.lodController.completeTransition();

    // Stats
    this.completedTransitions++;
    this.lastTransitionDuration = duration;
    this.transitionTimes.push(duration);
    if (this.transitionTimes.length > 10) this.transitionTimes.shift();

    // Reset coverage tracking for the now-current target
    this.resetCoverageTracking();

    this.emit('transitionCompleted');
  }

  /**
   * Batched, safety-checked removals
   */
  private async safelyRemovePendingTiles(): Promise<void> {
    if (this.pendingRemovalTiles.size === 0 || this.disposed) return;

    // Final safety check
    const tilesToRemove = Array.from(this.pendingRemovalTiles.keys());
    if (!this.replacementPolicy.isSafeToRemoveTiles(tilesToRemove)) {
      this.safetyCheckFailures++;
      this.pendingRemovalTiles.clear();
      this.emit('safetyCheckFailed', 'Final safety check failed before removal');
      return;
    }

    const coverage = this.replacementPolicy.analyzeCoverage();
    const initialBatchSize = this.replacementPolicy.getSafeBatchSize(
      tilesToRemove.length,
      coverage.totalTiles
    );

    let stopFurtherRemovals = false;

    await processBatches(
      tilesToRemove,
      Math.max(1, initialBatchSize),
      async (batch) => {
        if (this.disposed || stopFurtherRemovals) return;

        // Guard: do not remove if it would violate minimum visible tiles
        const remainingVisible = this.spatialIndex.getVisibleTiles().size;
        if (remainingVisible - batch.length < COVERAGE.MIN_REQUIRED_TILES) {
          // Stop all remaining removals for now; we'll try again after next transition
          stopFurtherRemovals = true;
          this.pendingRemovalTiles.clear();
          this.emit('safetyCheckFailed', 'Removal halted to preserve minimum visible tiles');
          return;
        }

        for (const tileKey of batch) {
          try {
            this.sceneAdapter.removeTileMesh(tileKey);
            this.pendingRemovalTiles.delete(tileKey);
          } catch (e) {
            // Log and continue; don't break the whole batch
            // Silently handle removal errors
            this.pendingRemovalTiles.delete(tileKey);
          }
        }

        this.emit('tilesRemoved', batch);
      },
      TIMING.REMOVAL_BATCH_DELAY
    );

    // If anything remains (e.g., stopped early), leave it queued for a later pass
  }

  /**
   * Emergency: force completion but keep safety. We still try a deferred cleanup.
   */
  forceCompleteTransition(): void {
    if (this.disposed) return;

    this.cancelScheduledTimers();

    // We do NOT wipe meshes aggressively here; we finalize the LOD…
    const duration = this.lodController.getTransitionElapsedTime();
    this.lodController.completeTransition();

    // …update stats…
    this.completedTransitions++;
    this.lastTransitionDuration = duration;
    this.transitionTimes.push(duration);
    if (this.transitionTimes.length > 10) this.transitionTimes.shift();

    // …and schedule a conservative cleanup once the viewport is stable again
    const id = setTimeout(() => {
      if (this.disposed) return;
      if (this.viewportService.isViewportStable(TIMING.REMOVAL_STABILITY_MS)) {
        void this.safelyRemovePendingTiles();
      }
    }, TIMING.REMOVAL_STABILITY_MS);
    this.timeoutCancelers.push(() => clearTimeout(id));

    // Reset coverage as target becomes current
    this.resetCoverageTracking();

    this.emit('transitionCompleted');
  }

  /**
   * Cancel timeouts and clear queued state (non-destructive)
   */
  cancelPendingOperations(): void {
    this.cancelScheduledTimers();
    this.pendingRemovalTiles.clear();
    this.loadedAllSet.clear();
    this.loadedByFace.clear();
    this.trackedTargetLevel = this.getTargetLevelNumber();
  }

  /**
   * Get transition statistics
   */
  getStats(): TransitionCoordinatorStats {
    const avg =
      this.transitionTimes.length > 0
        ? this.transitionTimes.reduce((s, t) => s + t, 0) / this.transitionTimes.length
        : 0;

    return {
      isTransitioning: this.lodController.getTransitionState() !== TransitionState.STABLE,
      pendingRemovals: this.pendingRemovalTiles.size,
      completedTransitions: this.completedTransitions,
      safetyCheckFailures: this.safetyCheckFailures,
      averageTransitionTime: avg,
      lastTransitionDuration: this.lastTransitionDuration,
    };
  }

  /**
   * Expose pending removal keys
   */
  getPendingRemovals(): string[] {
    return Array.from(this.pendingRemovalTiles.keys());
  }

  /**
   * Reset statistics counters
   */
  resetStats(): void {
    this.completedTransitions = 0;
    this.safetyCheckFailures = 0;
    this.transitionTimes.length = 0;
    this.lastTransitionDuration = 0;
  }

  /**
   * Event API
   */
  on<K extends keyof TransitionCoordinatorEvents>(event: K, handler: TransitionCoordinatorEvents[K]): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler);
  }

  off<K extends keyof TransitionCoordinatorEvents>(event: K, handler: TransitionCoordinatorEvents[K]): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) handlers.delete(handler);
  }

  private emit<K extends keyof TransitionCoordinatorEvents>(
    event: K,
    ...args: Parameters<TransitionCoordinatorEvents[K]>
  ): void {
    const handlers = this.eventHandlers.get(event);
    if (!handlers || handlers.size === 0) return;
    for (const handler of handlers) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (handler as any)(...args);
      } catch (error) {
        // Silently handle event errors
      }
    }
  }

  /**
   * Cleanup
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    this.cancelScheduledTimers();
    this.pendingRemovalTiles.clear();
    this.loadedAllSet.clear();
    this.loadedByFace.clear();

    // Unsubscribe from LOD controller events (if supported)
    try {
      this.lodController.off?.('transitionStarted', this.onTransitionStartedBound as any);
      this.lodController.off?.('lodChanged', this.onLodChangedBound as any);
    } catch {
      /* no-op for simple emitters without .off */
    }

    this.eventHandlers.clear();
    this.resetStats();
  }

  // ---------- helpers ----------
// Accept both controller styles: number OR { level, tilesPerFace }
private getTargetLevelNumber(): number {
  const t: any = this.lodController.getTargetLODLevel();
  return typeof t === 'number' ? t : t.level;
}

private getTargetTilesPerFace(): number {
  const t: any = this.lodController.getTargetLODLevel();
  if (typeof t === 'number') {
    // If your controller exposes a method to map level->tilesPerFace, use it:
    if (typeof (this.lodController as any).getTilesPerFace === 'function') {
      return (this.lodController as any).getTilesPerFace(t);
    }
    // Fallback guess if each level doubles tiles per side: tilesPerFace = 2^level
    // Replace this with your real mapping if different.
    return 1 << t;
  }
  return t.tilesPerFace;
}

  private cancelScheduledTimers(): void {
    while (this.timeoutCancelers.length) {
      const cancel = this.timeoutCancelers.pop()!;
      try {
        cancel();
      } catch {
        /* ignore */
      }
    }
  }

  private buildTileKey(face: CubeFace, level: number, x: number, y: number): string {
    return `${face}_L${level}_${x}_${y}`;
  }

  private buildSpatialKey(face: CubeFace, x: number, y: number): string {
    return `${face}_${x}_${y}`;
  }

  private parseTileKey(
    tileKey: string
  ): { face: CubeFace; level: number; x: number; y: number } | null {
    // Expected: FACE_L{level}_{x}_{y}
    const m = tileKey.match(/^([A-Z]+)_L(\d+)_(\d+)_(\d+)$/);
    if (!m) return null;
    return {
      face: m[1] as CubeFace,
      level: Number(m[2]),
      x: Number(m[3]),
      y: Number(m[4]),
    };
    }
}
